import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HStack,
  Heading,
  Input,
  NewButton,
  Paragraph,
  Tag,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';
import { DynamicArtifactTemplate } from '@eluve/llm-outputs';
import {
  useTenantIdFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { templateBuilderStore, useUploadUserFile } from '@eluve/smart-blocks';

const generateTemplateFormSchema = z.object({
  documentDescription: z
    .string()
    .min(1, 'Document description is required')
    .describe(
      'Description of the desired medical document format, including sections, fields, and structure requirements',
    ),
  documentType: z
    .string()
    .optional()
    .describe(
      'Type of medical document (e.g., "clinical note", "lab report", "discharge summary", "patient assessment")',
    ),
  requiredFields: z
    .string()
    .optional()
    .describe('Comma-separated list of required fields'),
  constraints: z
    .string()
    .optional()
    .describe(
      'Any specific constraints, validation rules, or formatting requirements',
    ),
  pdfFile: z.instanceof(File).optional(),
});

type GenerateTemplateFormValues = z.infer<typeof generateTemplateFormSchema>;

interface GenerateOutputTemplateProps {
  onTemplateGenerated: (template: DynamicArtifactTemplate) => void;
}

export const GenerateOutputTemplate: FC<GenerateOutputTemplateProps> = ({
  onTemplateGenerated,
}) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [uploadedPdfId, setUploadedPdfId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiClient = useApiClient();
  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromSession();

  const { uploadUserFileToGCS } = useUploadUserFile(tenantId!);

  const form = useForm<GenerateTemplateFormValues>({
    resolver: zodResolver(generateTemplateFormSchema),
    defaultValues: {
      documentDescription: '',
      documentType: '',
      requiredFields: '',
      constraints: '',
      pdfFile: undefined,
    },
    mode: 'onChange',
  });

  const documentDescription = form.watch('documentDescription');
  const isValid = Boolean(documentDescription?.trim());

  const handlePdfFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setIsUploadingPdf(true);
      const extension = file.name.split('.').pop();
      if (!extension) {
        toast.error('Invalid file extension');
        return;
      }

      const userUploadId = await uploadUserFileToGCS({
        file,
        mimeType: 'application/pdf',
        extension,
        fileType: 'CHAT_DOCUMENT',
      });

      if (!userUploadId) {
        toast.error('Failed to upload PDF file');
        return;
      }

      setUploadedPdfId(userUploadId);
      form.setValue('pdfFile', file);
      toast.success('PDF file uploaded successfully');
    } catch {
      toast.error('Failed to upload PDF file');
    } finally {
      setIsUploadingPdf(false);
    }
  };

  const handleRemovePdf = () => {
    setUploadedPdfId(null);
    form.setValue('pdfFile', undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (values: GenerateTemplateFormValues) => {
    setIsSubmitting(true);

    try {
      let presignedUrl: string | undefined = undefined;

      // Generate presigned URL if there's an uploaded file
      if (uploadedPdfId) {
        if (userId) {
          const presignedResponse = await apiClient.users.getUserFileSignedUrl({
            params: {
              tenantId: tenantId!,
              userId,
              fileId: uploadedPdfId,
            },
          });

          if (
            presignedResponse.status === 200 &&
            presignedResponse.body.presignedUrl
          ) {
            presignedUrl = presignedResponse.body.presignedUrl;
          }
        }
      }

      // Parse comma-separated required fields into array
      const requiredFieldsArray = values.requiredFields
        ?.split(',')
        .map((field) => field.trim())
        .filter(Boolean);

      const response = await apiClient.adminLlm.createOutputTemplate({
        body: {
          documentDescription: values.documentDescription,
          documentType: values.documentType ?? undefined,
          requiredFields: requiredFieldsArray?.length
            ? requiredFieldsArray
            : undefined,
          constraints: values.constraints ?? undefined,
          presignedUrl: presignedUrl,
        },
      });

      if (response.status === 201) {
        const { dynamicArtifact, hydrateJson } = response.body;
        onTemplateGenerated(dynamicArtifact);
        templateBuilderStore.send(hydrateJson);

        toast.success('Output template generated successfully!');

        // Reset form
        form.reset();
      } else if (response.status === 422) {
        const errorMessage =
          response.body.message ?? 'Failed to generate template';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Failed to generate output template', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack gap={4}>
      <Heading size="lg" weight="medium" color="primary">
        Generate Template with AI
      </Heading>
      <Paragraph size="md" weight="regular" color="secondary">
        Generate a new medical document template based on your requirements.
      </Paragraph>

      <Form {...form}>
        <form
          className="flex w-full flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="documentDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text size="md" color="tertiary">
                    Document Description{' '}
                    <span className="text-text-negative-subtle">
                      (Required)
                    </span>
                  </Text>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe the medical document format, including sections, fields, and structure requirements..."
                    disabled={isSubmitting}
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="documentType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text size="md" color="tertiary">
                    Type
                  </Text>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='e.g., "clinical note", "lab report", "discharge summary"'
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="requiredFields"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text size="md" color="tertiary">
                    Required Fields
                  </Text>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Comma-separated list: chief complaint, assessment, plan"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="constraints"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text size="md" color="tertiary">
                    Additional Instructions
                  </Text>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Any specific validation rules, formatting requirements, or constraints..."
                    disabled={isSubmitting}
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pdfFile"
            render={() => (
              <FormItem>
                <FormLabel>
                  <Text size="md" color="tertiary">
                    Example Document (PDF){'  '}
                    <Tag size="xs" color="teal" label="Beta" />
                  </Text>
                </FormLabel>
                <FormControl>
                  <VStack gap={2} align="stretch">
                    {uploadedPdfId ? (
                      <HStack
                        className="rounded-lg border border-border-primary bg-bg-primary p-3"
                        gap={3}
                        justify="between"
                      >
                        <HStack gap={2}>
                          <Text size="md" color="tertiary">
                            <span role="img" aria-label="PDF document">
                              📄
                            </span>
                            PDF uploaded successfully
                          </Text>
                        </HStack>
                        <NewButton
                          disabled={isSubmitting || isUploadingPdf}
                          icon={{ name: 'Trash' }}
                          onClick={handleRemovePdf}
                          type="outline"
                          size="s"
                        />
                      </HStack>
                    ) : (
                      <Input
                        ref={fileInputRef}
                        accept=".pdf"
                        className="rounded-lg border border-border-primary bg-bg-primary"
                        disabled={isSubmitting || isUploadingPdf}
                        onChange={handlePdfFileChange}
                        type="file"
                      />
                    )}
                    {isUploadingPdf && (
                      <Text size="sm" color="secondary">
                        Uploading PDF...
                      </Text>
                    )}
                  </VStack>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <NewButton
            disabled={!isValid}
            loading={isSubmitting}
            submit
            text="Generate Template"
            type="primary"
          />
        </form>
      </Form>
    </VStack>
  );
};
